'use client';

import { useState, useEffect } from 'react';
import { Button } from '@heroui/button';
import { HeroUIProvider } from '@heroui/react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/modal';
import { Input } from '@heroui/input';
import { Form } from '@heroui/form';
import { addToast, ToastProvider } from '@heroui/toast';
import { RegMobie } from '@/utils/regRule';
import * as api from './api';
import { Select, SelectItem } from '@heroui/select';
import { RightOutlined } from '@ant-design/icons';
import { getCurrentPosition } from '@/utils/location';
import VerificationCode from './components/VerificationCode';

interface Props {
  open?: boolean;
  onClose?: (value?: boolean) => void;
  shopId?: number; // 门店id
  extraParams?: { groupId?: number };
}


export default function Index({ open, onClose, shopId, extraParams }: Props) {
  const { groupId } = extraParams || {};
  const [smsCode, setSmsCode] = useState<string>();
  const [smsToken, setSmsToken] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [name, setName] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [regionList, setRegionList] = useState<api.RegionTreeResult[]>([]);
  const [cityList, setCityList] = useState<api.RegionItem[]>([]);
  const [regionSelect, setRegionSelect] = useState<api.RegionItem>();
  const [citySelect, setCitySelect] = useState<api.RegionItem>();

  useEffect(() => {
    if (open) {
      inintialize();
    }
  }, [open]);

  async function inintialize() {
    try {
      const res = await getCurrentPosition() as { latitude: number; longitude: number };
      const _region = await api.fetchReginTreeApi({
        groupId,
        type: 1,
      });
      setRegionList(_region || []);
      const location = await api.fetchLocationApi({
        lat: res?.latitude,
        lng: res?.longitude,
      });
      const matchRegion = _region?.find(v => v.bh === location?.provinceCode) || {};
      const matchCity = matchRegion?.shopRegionList?.find(v => v.bh === location?.cityCode) || {};
      if (matchRegion?.bh && matchCity?.bh) {
        setCityList(matchRegion?.shopRegionList || []);
        setRegionSelect({bh: location?.provinceCode, name: location?.provinceName});
        setCitySelect({bh: location?.cityCode, name: location?.cityName});
      }
    } catch (e: any) {
      console.log("error:",e)
      // addToast({ description: e?.message, color: 'danger', icon: 'none', timeout: 2000 });
    }
  }

  function onRegionChange(value?: string) {
    const area: api.RegionTreeResult = regionList?.find(v => v.bh === value) || {};
    setRegionSelect(area || {});
    setCitySelect({});
    setCityList(area?.shopRegionList || []);
  }

  function onAreaChange(value?: string) {
    const area: api.RegionItem = cityList?.find(v => v.bh === value) || {};
    setCitySelect(area || {});
  }

  function onOpenChange() {
    onClose?.();
  }

  function formSubmit() {
    if (!name) {
      addToast({ description: '请输入您的称呼！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!phone || !RegMobie.test(phone)) {
      addToast({ description: '请输入正确的手机号！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!smsToken) {
      addToast({ description: '请先发送验证码！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!smsCode) {
      addToast({ description: '请输入有效的4位验证码！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    checkChangeCountAfter();
  };

  async function checkChangeCountAfter() {
    setLoading(true);
    try {
      await api.fetchClueRegisterApi({
        mobile: phone,
        smsCode,
        smsToken,
        name,
        shopId,
      });
      setLoading(false);
      addToast({description: '预约成功！', color: 'success', timeout: 2000})
      onClose?.();
    } catch (e: any) {
      setLoading(false);
      addToast({ description: e.message, color: 'danger', icon: 'none', timeout: 2000 });
    }
  };

  return (
    <HeroUIProvider>
      <ToastProvider maxVisibleToasts={1} placement="top-center" />
      <Modal className="p-0 m-0 rounded-b-none" isOpen={open} placement="bottom" onOpenChange={onOpenChange}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col items-center text-base text-[#333] dark:text-white border-b border-solid border-[#eee] dark:border-[#0a0a0a] gap-1">提交信息</ModalHeader>
              <ModalBody className="p-0">
                <Form className="gap-0">
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Input
                      radius="none"
                      errorMessage="请输入您的称呼"
                      label="您的称呼"
                      labelPlacement="outside-left"
                      name="name"
                      classNames={{
                        inputWrapper: 'bg-transparent items-center shadow-none group-data-[focus=true]:bg-transparent',
                        label: 'min-w-[70px] text-[15px] text-[#333] dark:text-white',
                        mainWrapper: 'w-full',
                        base: 'data-[has-helper=true]:items-baseline',
                        input: 'group-data-[focus=true]:outline-none',
                      }}
                      placeholder="请输入您的称呼"
                      type="text"
                      onValueChange={(value: string) => setName(value)}
                    />
                  </div>
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Input
                      className="bg-transparent"
                      radius="none"
                      classNames={{
                        inputWrapper: 'bg-transparent shadow-none group-data-[focus=true]:bg-transparent',
                        label: 'min-w-[70px] text-[15px] text-[#333] dark:text-white',
                        mainWrapper: 'w-full',
                        base: 'data-[has-helper=true]:items-baseline',
                        input: 'group-data-[focus=true]:outline-none',
                      }}
                      errorMessage="请输入您的手机号码"
                      label="手机号码"
                      labelPlacement="outside-left"
                      name="mobile"
                      placeholder="请输入手机号码"
                      onValueChange={(value: string) => setPhone(value)}
                      validate={(value: string) => {
                        if (!RegMobie.test(value)) {
                          return '请输入正确的手机号码';
                        }
                        return true;
                      }}
                      type="tel"
                    />
                  </div>
                  <VerificationCode setSmsCode={setSmsCode} setSmsToken={setSmsToken} phone={phone} />
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Select
                      radius="none"
                      aria-label="省"
                      classNames={{
                        trigger: 'bg-transparent',
                        selectorIcon: 'text-[#ccc] dark:text-white',
                      }}
                      errorMessage="请选择"
                      name="shopId"
                      placeholder="请选择"
                      selectedKeys={regionSelect?.bh ? [regionSelect.bh] : []}
                      disableSelectorIconRotation
                      selectorIcon={<RightOutlined style={{ color: '#ccc' }} />}
                      onChange={e => onRegionChange(e.target.value)}
                    >
                      {regionList?.map(v => <SelectItem key={v.bh}>{v.name}</SelectItem>)}
                    </Select>
                    <Select
                      radius="none"
                      aria-label="市"
                      classNames={{
                        selectorIcon: 'text-[#ccc] dark:text-white',
                        trigger: 'bg-transparent',
                        // trigger: 'bg-transparent data-[hover=true]:bg-transparent',
                      }}
                      errorMessage="请选择"
                      disableSelectorIconRotation
                      selectedKeys={citySelect?.bh ? [citySelect.bh] : []}
                      onChange={e => onAreaChange(e.target.value)}
                      name="shopId"
                      placeholder="请选择"
                      selectorIcon={<RightOutlined style={{ color: '#ccc' }} />}
                    >
                      {cityList?.map(v => <SelectItem key={v.bh}>{v.name}</SelectItem>)}
                    </Select>
                  </div>
                </Form>
              </ModalBody>
              <ModalFooter className="flex flex-col mt-[10px] pl-[15px] pr-[15px]">
                <Button
                  style={{ backgroundImage: 'linear-gradient(90deg, #FFAE01 0%, #FF921C 100%)' }}
                  isLoading={loading}
                  color="warning"
                  onPress={formSubmit}
                  className="bg-gradient-to-tr from-[#FFAE01] to-[#FF921C] text-white shadow-lg grow rounded-[4px] h-[49px] text-[16px]"
                >
                  立即预约
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </HeroUIProvider>
  );
}
